/**
 * The browse taxonomy for the connector catalog — "CRM & Sales", "Communication",
 * and so on — as used by the Connectors page's category filter.
 *
 * Not to be confused with `Connector.category`, which despite the name carries a
 * TIER ("managed" for platform-owned rows, "Custom" for an org's own) and gates
 * read-only UI in ConnectorDetailPage and ConnectorsPage. That field is derived
 * server-side from the owning org and was never a taxonomy; this module is the
 * taxonomy, and it is surfaced as `categoryKey` so the two never collide.
 *
 * The key set is copied VERBATIM from UCL's `src/lib/categories.ts`, which owns
 * `authored_connector.category` — the authoritative value for every migrated org,
 * and therefore for all of dev and prod. Keeping the keys identical is what lets
 * a category authored in UCL's admin console resolve here without a translation
 * table. Adding a key means adding it in both repos.
 */
/** Ordered for display: broad/popular buckets first, `other` last. */
export const CONNECTOR_CATEGORIES = [
    { key: "ai-ml", label: "AI & ML" },
    { key: "developer-tools", label: "Developer Tools" },
    { key: "search-web", label: "Search & Web" },
    { key: "data-database", label: "Data & Databases" },
    { key: "cloud-devops", label: "Cloud & DevOps" },
    { key: "crm-sales", label: "CRM & Sales" },
    { key: "marketing", label: "Marketing" },
    { key: "finance-payments", label: "Finance & Payments" },
    { key: "ecommerce", label: "E-commerce" },
    { key: "communication", label: "Communication" },
    { key: "social-media", label: "Social Media" },
    { key: "productivity", label: "Productivity" },
    { key: "project-management", label: "Project Management" },
    { key: "docs-knowledge", label: "Docs & Knowledge" },
    { key: "analytics-monitoring", label: "Analytics & Monitoring" },
    { key: "security", label: "Security" },
    { key: "it-service", label: "IT & Service Management" },
    { key: "design", label: "Design" },
    { key: "maps-location", label: "Maps & Location" },
    { key: "entertainment-media", label: "Entertainment & Media" },
    { key: "other", label: "Other" },
];
/** The bucket everything unrecognised lands in, so nothing drops out of the catalog. */
export const OTHER_CATEGORY = "other";
const LABELS = new Map(CONNECTOR_CATEGORIES.map((c) => [c.key, c.label]));
/** Taxonomy keys indexed by their own normalized form, so "AI & ML", "ai_ml" and
 *  "AI-ML" all resolve to `ai-ml` without a second hand-written alias table. */
const KEY_BY_NORMALIZED = new Map();
for (const c of CONNECTOR_CATEGORIES) {
    KEY_BY_NORMALIZED.set(normalize(c.key), c.key);
    KEY_BY_NORMALIZED.set(normalize(c.label), c.key);
}
/**
 * Values that appear in the `category` field but are not taxonomy — fastn's own
 * tier markers, and the empty default the column ships with. Treated as "no
 * category was ever set", which is what sends a row to the slug map below.
 */
const TIER_SENTINELS = new Set(["", "custom", "managed", "community", "platform"]);
/** Strips case and every separator, so slug/key/label spellings collapse together. */
function normalize(value) {
    return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}
/**
 * Curated slug -> category for connectors whose authored category is missing or
 * is still a tier sentinel.
 *
 * Keys are normalized on lookup, so an entry written as "google-calendar" also
 * matches fastn's own `googlecalendar` spelling and UCL's `google_calendar`.
 * A slug absent here is not an error — it resolves to `other` and stays
 * browsable. The durable fix for a wrong or missing entry is to set the
 * connector's category in UCL's admin console, which outranks this map.
 */
export const CONNECTOR_CATEGORY_BY_SLUG = {
    // ── CRM & Sales ────────────────────────────────────────────────────────────
    salesforce: "crm-sales",
    hubspot: "crm-sales",
    pipedrive: "crm-sales",
    zohocrm: "crm-sales",
    zoho: "crm-sales",
    dynamics365: "crm-sales",
    microsoftdynamics: "crm-sales",
    close: "crm-sales",
    copper: "crm-sales",
    insightly: "crm-sales",
    freshsales: "crm-sales",
    keap: "crm-sales",
    sugarcrm: "crm-sales",
    attio: "crm-sales",
    folk: "crm-sales",
    apollo: "crm-sales",
    outreach: "crm-sales",
    salesloft: "crm-sales",
    gong: "crm-sales",
    clari: "crm-sales",
    lusha: "crm-sales",
    zoominfo: "crm-sales",
    clearbit: "crm-sales",
    dealhub: "crm-sales",
    pandadoc: "crm-sales",
    docusign: "crm-sales",
    hellosign: "crm-sales",
    proposify: "crm-sales",
    // ── Communication ──────────────────────────────────────────────────────────
    slack: "communication",
    microsoftteams: "communication",
    discord: "communication",
    zoom: "communication",
    googlemeet: "communication",
    webex: "communication",
    twilio: "communication",
    vonage: "communication",
    messagebird: "communication",
    telegram: "communication",
    whatsapp: "communication",
    gmail: "communication",
    outlook: "communication",
    microsoftoutlook: "communication",
    sendgrid: "communication",
    mailgun: "communication",
    postmark: "communication",
    resend: "communication",
    frontapp: "communication",
    front: "communication",
    ringcentral: "communication",
    dialpad: "communication",
    aircall: "communication",
    // ── Productivity ───────────────────────────────────────────────────────────
    googlecalendar: "productivity",
    googledrive: "productivity",
    googlesheets: "productivity",
    googledocs: "productivity",
    googleforms: "productivity",
    googleslides: "productivity",
    googleworkspace: "productivity",
    microsoft365: "productivity",
    onedrive: "productivity",
    sharepoint: "productivity",
    excel: "productivity",
    dropbox: "productivity",
    box: "productivity",
    calendly: "productivity",
    cal: "productivity",
    calcom: "productivity",
    todoist: "productivity",
    evernote: "productivity",
    zapier: "productivity",
    make: "productivity",
    ifttt: "productivity",
    typeform: "productivity",
    jotform: "productivity",
    surveymonkey: "productivity",
    docusignclick: "productivity",
    // ── Project Management ─────────────────────────────────────────────────────
    jira: "project-management",
    linear: "project-management",
    asana: "project-management",
    trello: "project-management",
    clickup: "project-management",
    monday: "project-management",
    basecamp: "project-management",
    wrike: "project-management",
    smartsheet: "project-management",
    shortcut: "project-management",
    height: "project-management",
    productboard: "project-management",
    aha: "project-management",
    teamwork: "project-management",
    azuredevops: "project-management",
    youtrack: "project-management",
    // ── Docs & Knowledge ───────────────────────────────────────────────────────
    notion: "docs-knowledge",
    confluence: "docs-knowledge",
    coda: "docs-knowledge",
    airtable: "docs-knowledge",
    guru: "docs-knowledge",
    slab: "docs-knowledge",
    document360: "docs-knowledge",
    gitbook: "docs-knowledge",
    readme: "docs-knowledge",
    mediawiki: "docs-knowledge",
    obsidian: "docs-knowledge",
    quip: "docs-knowledge",
    // ── Developer Tools ────────────────────────────────────────────────────────
    github: "developer-tools",
    gitlab: "developer-tools",
    bitbucket: "developer-tools",
    sentry: "developer-tools",
    circleci: "developer-tools",
    buildkite: "developer-tools",
    jenkins: "developer-tools",
    travisci: "developer-tools",
    sonarqube: "developer-tools",
    npm: "developer-tools",
    dockerhub: "developer-tools",
    postman: "developer-tools",
    swagger: "developer-tools",
    launchdarkly: "developer-tools",
    triggerdev: "developer-tools",
    codeclimate: "developer-tools",
    sourcegraph: "developer-tools",
    raygun: "developer-tools",
    bugsnag: "developer-tools",
    rollbar: "developer-tools",
    // ── Cloud & DevOps ─────────────────────────────────────────────────────────
    aws: "cloud-devops",
    awss3: "cloud-devops",
    s3: "cloud-devops",
    awslambda: "cloud-devops",
    gcp: "cloud-devops",
    googlecloud: "cloud-devops",
    azure: "cloud-devops",
    digitalocean: "cloud-devops",
    heroku: "cloud-devops",
    vercel: "cloud-devops",
    netlify: "cloud-devops",
    render: "cloud-devops",
    fly: "cloud-devops",
    railway: "cloud-devops",
    cloudflare: "cloud-devops",
    kubernetes: "cloud-devops",
    terraform: "cloud-devops",
    pulumi: "cloud-devops",
    pagerduty: "cloud-devops",
    opsgenie: "cloud-devops",
    cubecloud: "cloud-devops",
    // ── Data & Databases ───────────────────────────────────────────────────────
    postgresql: "data-database",
    postgres: "data-database",
    mysql: "data-database",
    mssql: "data-database",
    sqlserver: "data-database",
    mongodb: "data-database",
    redis: "data-database",
    snowflake: "data-database",
    bigquery: "data-database",
    redshift: "data-database",
    databricks: "data-database",
    supabase: "data-database",
    firebase: "data-database",
    elasticsearch: "data-database",
    clickhouse: "data-database",
    dynamodb: "data-database",
    cassandra: "data-database",
    fivetran: "data-database",
    airbyte: "data-database",
    dbt: "data-database",
    rudderstack: "data-database",
    segment: "data-database",
    sftp: "data-database",
    ftps: "data-database",
    // ── Analytics & Monitoring ─────────────────────────────────────────────────
    googleanalytics: "analytics-monitoring",
    mixpanel: "analytics-monitoring",
    amplitude: "analytics-monitoring",
    posthog: "analytics-monitoring",
    heap: "analytics-monitoring",
    datadog: "analytics-monitoring",
    newrelic: "analytics-monitoring",
    grafana: "analytics-monitoring",
    prometheus: "analytics-monitoring",
    splunk: "analytics-monitoring",
    looker: "analytics-monitoring",
    tableau: "analytics-monitoring",
    powerbi: "analytics-monitoring",
    metabase: "analytics-monitoring",
    hotjar: "analytics-monitoring",
    fullstory: "analytics-monitoring",
    visualping: "analytics-monitoring",
    octolens: "analytics-monitoring",
    // ── Finance & Payments ─────────────────────────────────────────────────────
    stripe: "finance-payments",
    paypal: "finance-payments",
    square: "finance-payments",
    adyen: "finance-payments",
    braintree: "finance-payments",
    quickbooks: "finance-payments",
    xero: "finance-payments",
    netsuite: "finance-payments",
    sage: "finance-payments",
    freshbooks: "finance-payments",
    bill: "finance-payments",
    ramp: "finance-payments",
    brex: "finance-payments",
    expensify: "finance-payments",
    chargebee: "finance-payments",
    recurly: "finance-payments",
    paddle: "finance-payments",
    plaid: "finance-payments",
    wise: "finance-payments",
    coinbase: "finance-payments",
    gusto: "finance-payments",
    rippling: "finance-payments",
    workday: "finance-payments",
    bamboohr: "finance-payments",
    greenhouse: "finance-payments",
    lever: "finance-payments",
    // ── E-commerce ─────────────────────────────────────────────────────────────
    shopify: "ecommerce",
    woocommerce: "ecommerce",
    magento: "ecommerce",
    bigcommerce: "ecommerce",
    squarespace: "ecommerce",
    wix: "ecommerce",
    etsy: "ecommerce",
    amazonsellercentral: "ecommerce",
    ebay: "ecommerce",
    walmart: "ecommerce",
    cin7: "ecommerce",
    cin7core: "ecommerce",
    shipstation: "ecommerce",
    shippo: "ecommerce",
    aftership: "ecommerce",
    lightspeed: "ecommerce",
    prestashop: "ecommerce",
    // ── Marketing ──────────────────────────────────────────────────────────────
    mailchimp: "marketing",
    klaviyo: "marketing",
    braze: "marketing",
    customerio: "marketing",
    activecampaign: "marketing",
    marketo: "marketing",
    pardot: "marketing",
    constantcontact: "marketing",
    sendinblue: "marketing",
    brevo: "marketing",
    iterable: "marketing",
    onesignal: "marketing",
    googleads: "marketing",
    facebookads: "marketing",
    metaads: "marketing",
    linkedinads: "marketing",
    tiktokads: "marketing",
    semrush: "marketing",
    ahrefs: "marketing",
    producthunt: "marketing",
    trustradius: "marketing",
    g2: "marketing",
    // ── Social Media ───────────────────────────────────────────────────────────
    linkedin: "social-media",
    twitter: "social-media",
    x: "social-media",
    facebook: "social-media",
    instagram: "social-media",
    tiktok: "social-media",
    youtube: "social-media",
    reddit: "social-media",
    pinterest: "social-media",
    mastodon: "social-media",
    bluesky: "social-media",
    buffer: "social-media",
    hootsuite: "social-media",
    // ── IT & Service Management ────────────────────────────────────────────────
    servicenow: "it-service",
    zendesk: "it-service",
    freshservice: "it-service",
    freshdesk: "it-service",
    intercom: "it-service",
    helpscout: "it-service",
    jiraservicemanagement: "it-service",
    ivanti: "it-service",
    solarwinds: "it-service",
    jamf: "it-service",
    kandji: "it-service",
    intune: "it-service",
    hp: "it-service",
    hpworkforceexperience: "it-service",
    dell: "it-service",
    lenovo: "it-service",
    // ── Security ───────────────────────────────────────────────────────────────
    okta: "security",
    auth0: "security",
    onelogin: "security",
    duo: "security",
    crowdstrike: "security",
    snyk: "security",
    onepassword: "security",
    lastpass: "security",
    bitwarden: "security",
    hashicorpvault: "security",
    cloudflarezerotrust: "security",
    keycloak: "security",
    vanta: "security",
    drata: "security",
    // ── AI & ML ────────────────────────────────────────────────────────────────
    openai: "ai-ml",
    anthropic: "ai-ml",
    googlegemini: "ai-ml",
    gemini: "ai-ml",
    mistral: "ai-ml",
    cohere: "ai-ml",
    huggingface: "ai-ml",
    replicate: "ai-ml",
    pinecone: "ai-ml",
    weaviate: "ai-ml",
    qdrant: "ai-ml",
    chroma: "ai-ml",
    langsmith: "ai-ml",
    elevenlabs: "ai-ml",
    assemblyai: "ai-ml",
    deepgram: "ai-ml",
    stabilityai: "ai-ml",
    perplexity: "ai-ml",
    // ── Search & Web ───────────────────────────────────────────────────────────
    googlesearch: "search-web",
    bing: "search-web",
    algolia: "search-web",
    typesense: "search-web",
    meilisearch: "search-web",
    tavily: "search-web",
    firecrawl: "search-web",
    serpapi: "search-web",
    exa: "search-web",
    brave: "search-web",
    apify: "search-web",
    scrapingbee: "search-web",
    // ── Design ─────────────────────────────────────────────────────────────────
    figma: "design",
    canva: "design",
    sketch: "design",
    adobe: "design",
    invision: "design",
    miro: "design",
    mural: "design",
    framer: "design",
    webflow: "design",
    cloudinary: "design",
    unsplash: "design",
    // ── Maps & Location ────────────────────────────────────────────────────────
    googlemaps: "maps-location",
    mapbox: "maps-location",
    hereapi: "maps-location",
    openstreetmap: "maps-location",
    geoapify: "maps-location",
    // ── Entertainment & Media ──────────────────────────────────────────────────
    spotify: "entertainment-media",
    vimeo: "entertainment-media",
    twitch: "entertainment-media",
    soundcloud: "entertainment-media",
    contentful: "entertainment-media",
    sanity: "entertainment-media",
    strapi: "entertainment-media",
    wordpress: "entertainment-media",
    ghost: "entertainment-media",
    // ── Other ──────────────────────────────────────────────────────────────────
    ycombinator: "other",
};
const CATEGORY_BY_NORMALIZED_SLUG = new Map(Object.entries(CONNECTOR_CATEGORY_BY_SLUG).map(([slug, key]) => [normalize(slug), key]));
/**
 * Human label for a category key. Known keys map to their curated label; an
 * unknown/legacy value is prettified ("search-web" -> "Search Web") so the UI
 * never shows a raw slug.
 */
export function categoryLabel(key) {
    if (!key)
        return "";
    const known = LABELS.get(key);
    if (known)
        return known;
    return key
        .replace(/[-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (m) => m.toUpperCase());
}
/** True when `key` is one of the taxonomy's own keys. */
export function isConnectorCategory(key) {
    return !!key && LABELS.has(key);
}
/**
 * The taxonomy key to browse a connector under.
 *
 * Precedence, and the reason for it:
 *  1. The authored category, when it is actually taxonomy. UCL's admin console
 *     is where a human sets this, so it has to outrank anything hardcoded here
 *     — otherwise correcting a miscategorised connector would be impossible
 *     without shipping a fastn release.
 *  2. The curated slug map, for the catalog's long tail, which today carries no
 *     authored category at all (fastn only ever wrote the tier sentinel).
 *  3. `other`, never empty — a connector with no category still has to appear
 *     in the catalog, and a blank chip is not a bucket anyone can click.
 */
export function resolveConnectorCategory(connector) {
    const authored = (connector.category ?? "").trim();
    if (authored && !TIER_SENTINELS.has(authored.toLowerCase())) {
        const known = KEY_BY_NORMALIZED.get(normalize(authored));
        if (known)
            return known;
    }
    const slug = (connector.slug ?? "").trim();
    if (slug) {
        const mapped = CATEGORY_BY_NORMALIZED_SLUG.get(normalize(slug));
        if (mapped)
            return mapped;
    }
    return OTHER_CATEGORY;
}
//# sourceMappingURL=connector-categories.js.map